import { useDynamicList, useUpdate } from 'ahooks'
import clsx from 'clsx'
import moment, { Moment } from 'moment'
import {
  Button,
  Card,
  ColumnsType,
  Form,
  Icon,
  Info,
  Input,
  Table,
  TimePicker,
  Title,
} from 'components/common'
import { objectEqual } from 'utils/utils'

export interface Variant {
  id?: string
  start?: Moment | string
  end?: Moment | string
  rate?: number
  position?: number
}

interface DynamicTableProps {
  initialValue?: Variant[]
}

const DynamicTable = ({ initialValue }: DynamicTableProps): JSX.Element => {
  const update = useUpdate()
  const { list, remove, getKey, push, replace } = useDynamicList<Variant>(
    initialValue || []
  )

  const columns: ColumnsType = [
    {
      key: 'start',
      dataIndex: 'start',
      title: '開始時刻',
      render: (text: string, row: Variant, index: number) => (
        <>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldError }) => (
              <Form.Item
                name={[index, 'start']}
                initialValue={text}
                rules={[
                  {
                    required: true,
                    message: 'このフィールドは必須です。',
                  },
                  {
                    pattern: /[0-9]{1,2}:[0-9]{1,2}/g,
                    message: 'HH:mm 形式で入力してください。',
                  },
                  ({ getFieldValue, setFields }) => ({
                    validator(_, value) {
                      let end = getFieldValue(['variants', index, 'end'])
                      if (typeof end === 'string') {
                        end = moment(end, 'HH:mm')
                      }

                      if (value > end) {
                        // 開始時間が終了時間より大きい場合は、それぞれの値を入れ替える
                        setFields([
                          { name: ['variants', index, 'start'], value: end },
                          { name: ['variants', index, 'end'], value },
                        ])
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
                noStyle={
                  getFieldError(['variants', index, 'start']).length === 0
                }
              >
                <TimePicker
                  onChange={(time) => {
                    // Even the return type is Moment, its giving Type error
                    // eslint-disable-next-line  @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    replace(index, { ...list[index], start: time })
                    update()
                  }}
                />
              </Form.Item>
            )}
          </Form.Item>
        </>
      ),
    },
    {
      key: 'end',
      dataIndex: 'end',
      title: '終了時刻',
      render: (text: string, row: Variant, index: number) => (
        <>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldError }) => (
              <Form.Item
                name={[index, 'end']}
                initialValue={text}
                rules={[
                  {
                    required: true,
                    message: 'このフィールドは必須です。',
                  },
                  {
                    pattern: /[0-9]{1,2}:[0-9]{1,2}/g,
                    message: 'HH:mm 形式で入力してください。',
                  },
                  ({ getFieldValue, setFields }) => ({
                    validator(_, value) {
                      let start = getFieldValue(['variants', index, 'start'])
                      if (typeof start === 'string') {
                        start = moment(start, 'HH:mm')
                      }

                      if (value < start) {
                        // 終了時間が開始時間より小さい場合は、それぞれの値を入れ替える
                        setFields([
                          { name: ['variants', index, 'start'], value },
                          { name: ['variants', index, 'end'], value: start },
                        ])
                      }
                      return Promise.resolve()
                    },
                  }),
                ]}
                noStyle={getFieldError(['variants', index, 'end']).length === 0}
              >
                <TimePicker
                  onChange={(time) => {
                    // Even the return type is Moment, its giving Type error
                    // eslint-disable-next-line  @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    replace(index, { ...list[index], end: time })
                    update()
                  }}
                />
              </Form.Item>
            )}
          </Form.Item>
        </>
      ),
    },
    {
      key: 'rate',
      dataIndex: 'rate',
      title: '料金倍率',
      className: clsx('hidden'),
      render: (text: string, row: Variant, index: number) => (
        <>
          <Form.Item name={[index, 'rate']} initialValue={text} hidden>
            <Input />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'id',
      dataIndex: 'id',
      title: 'ID',
      className: clsx('hidden'),
      render: (text: string, row: Variant, index: number) => (
        <>
          <Form.Item name={[index, 'id']} initialValue={text} hidden>
            <Input />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'position',
      dataIndex: 'position',
      title: 'ポジション',
      className: clsx('hidden'),
      render: (text: string, row: Variant, index: number) => (
        <>
          <Form.Item name={[index, 'position']} initialValue={text} hidden>
            <Input />
          </Form.Item>
        </>
      ),
    },
    {
      key: 'action',
      fixed: 'right',
      render: (_text, _row, index) => (
        <>
          <Button.Group>
            <Button
              danger
              onClick={() => remove(index)}
              icon={<Icon.DeleteFilled />}
            />
          </Button.Group>
        </>
      ),
    },
  ]

  return (
    <>
      {/* variants のバリデーションメッセージを表示するための FormItem */}
      <Form.List
        name="variants"
        rules={[
          {
            validator: (_, variants) => {
              if (
                Array.isArray(variants) &&
                variants.length > 0 &&
                // 空のオブジェクト（`{}`）じゃないかを検証
                variants[0] &&
                // UI 上何も表示されていなくてもデフォルトで start と end に空文字がセットされているのでこの判定が必要
                Object.entries(variants[0]).every(
                  ([_, val]) =>
                    typeof val !== 'undefined' && val !== null && val !== ''
                )
              ) {
                return Promise.resolve()
              }

              return Promise.reject(
                new Error('バリエーションは必ず一つ以上設定してください。')
              )
            },
          },
        ]}
      >
        {(_data, _operation, { errors }) => (
          <>
            <Table
              columns={columns}
              dataSource={list}
              rowKey={(record: Variant) => {
                const index = list.findIndex((elem) =>
                  objectEqual(elem, record)
                )
                return getKey(index).toString()
              }}
              pagination={false}
            />
            <Button
              style={{ marginTop: 8 }}
              block
              type="dashed"
              onClick={() => push({ rate: 1, position: list.length + 1 })}
            >
              + 追加する
            </Button>
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>
    </>
  )
}

export interface VariantsProps {
  initialValue?: Variant[]
  loading?: boolean
}

export const VariantInputs = ({
  initialValue,
  loading,
}: VariantsProps): JSX.Element => (
  <>
    <Card shadow loading={loading}>
      <div className={clsx('mb-8', 'flex', 'justify-between', 'items-center')}>
        <Info title="この利用時間区分で選べる時間帯を指定することができます。システムの利用者はここで作成された時間帯から時間を選択して予約をすることになります。">
          <Title level={5} space={'none'}>
            バリエーション
          </Title>
        </Info>
      </div>

      {loading === false && <DynamicTable initialValue={initialValue} />}
    </Card>
  </>
)
